import React, { useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import localeData from 'dayjs/plugin/localeData';
import { Calendar, Col, Row, Select } from 'antd';
import LeftIcon from '@/assets/images/greyIcons/filled.png';
import RightIcon from '@/assets/images/greyIcons/Icon.png';
import ArrowDown from '@/assets/icons/down-filled.png';
import './LogiblocksDTDPicker.scss';

dayjs.extend(localeData);

interface CalendarNavButtonProps {
  onClick: () => void;
  src: string;
  alt: string;
  width: string;
}

const CalendarNavButton: React.FC<CalendarNavButtonProps> = ({
  onClick,
  src,
  alt,
  width,
}) => (
  <button className='lb-date-calendar__nav-btn' type='button' onClick={onClick}>
    <img width={width} src={src} alt={alt} />
  </button>
);

interface CalendarSelectProps {
  value: number;
  onChange: (val: number) => void;
  className?: string;
  options: { value: number; label: string | number }[];
}

const CalendarSelect: React.FC<CalendarSelectProps> = ({
  value,
  onChange,
  className,
  options,
}) => (
  <Select
    className={className}
    value={value}
    onChange={onChange}
    options={options}
    popupMatchSelectWidth={false}
    getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
    suffixIcon={<img src={ArrowDown} alt='arrow' />}
  />
);

interface LogiblocksDateCalendarProps {
  /** Selected dates, as 'YYYY-MM-DD' strings. */
  values: string[];
  /** Toggles one date in or out of the selection. */
  onToggleDate: (isoDate: string) => void;
}

/** A month calendar for toggling any number of dates in and out of a selection. */
const LogiblocksDateCalendar: React.FC<LogiblocksDateCalendarProps> = ({
  values,
  onToggleDate,
}) => {
  const buttonPress = useRef(false);
  const [viewDate, setViewDate] = useState<Dayjs>(dayjs());
  const selectedSet = new Set(values);

  const handleSelect = (val: Dayjs) => {
    if (buttonPress.current) {
      buttonPress.current = false;
      return;
    }
    onToggleDate(val.format('YYYY-MM-DD'));
  };

  return (
    <div className='lb-date-calendar'>
      <Calendar
        value={viewDate}
        onChange={(v) => setViewDate(v as Dayjs)}
        fullscreen={false}
        onSelect={handleSelect}
        fullCellRender={(date) => {
          const cellDate = dayjs(date);
          const isSelected = selectedSet.has(cellDate.format('YYYY-MM-DD'));
          return (
            <div
              className={`lb-date-calendar__cell${isSelected ? ' lb-date-calendar__cell--selected' : ''}`}
            >
              {cellDate.date()}
            </div>
          );
        }}
        headerRender={({ value: hVal, onChange: hChange }) => {
          const months = dayjs.monthsShort();
          const monthOptions = months.map((m, i) => ({ value: i, label: m }));
          const year = hVal.year();
          const month = hVal.month();
          // Last year through five years ahead.
          const currentYear = dayjs().year();
          const yearOptions = Array.from({ length: 7 }, (_, i) => ({
            value: currentYear - 1 + i,
            label: currentYear - 1 + i,
          }));

          const handlePrev = () => {
            buttonPress.current = true;
            hChange(hVal.clone().subtract(1, 'month'));
          };
          const handleNext = () => {
            buttonPress.current = true;
            hChange(hVal.clone().add(1, 'month'));
          };

          return (
            <Row gutter={8} className='lb-date-calendar__header-row'>
              <Col span={11} className='lb-date-calendar__header-col'>
                <CalendarNavButton
                  onClick={handlePrev}
                  src={LeftIcon}
                  alt='prev'
                  width='12px'
                />
                <CalendarSelect
                  className='date-select-month lb-date-calendar__month-select'
                  value={month}
                  options={monthOptions}
                  onChange={(newMonth: number) => {
                    buttonPress.current = true;
                    hChange(hVal.clone().month(newMonth));
                  }}
                />
              </Col>
              <Col span={11} className='lb-date-calendar__header-col'>
                <CalendarSelect
                  className='my-year-select date-select-month lb-date-calendar__year-select'
                  value={year}
                  options={yearOptions}
                  onChange={(newYear: number) => {
                    buttonPress.current = true;
                    hChange(hVal.clone().year(newYear));
                  }}
                />
                <CalendarNavButton
                  onClick={handleNext}
                  src={RightIcon}
                  alt='next'
                  width='26px'
                />
              </Col>
            </Row>
          );
        }}
      />
    </div>
  );
};

export default LogiblocksDateCalendar;
